import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InstantFeedbackRatingBody, InstantFeedbackRatingResponse, InstantFeedbackUserResponse, ListInstantFeedbackQuery, PaginatedInstantFeedbackUserResponse, PopulatedInstantFeedbackUserResponse } from '@libs/instant-feedback/config';
import { CreateOpenFeedbackBody, ListOpenFeedbackQuery, OpenFeedbackResponse, PaginatedOpenFeedbackCategoryResponse, PaginatedOpenFeedbackResponse } from '@libs/open-feedback/config';
import { Observable, map } from 'rxjs';
import { PaginationSortingQuery, PresignedStorageResource } from '../../../../../libs/shared/nest-utils/src';
import { EnvironmentService } from '../@core/services/environment.service';

@Injectable({
  providedIn: 'root',
})
export class FeedbacksService {
  private readonly apiUrlInstantFeedbacks: string = '';
  private readonly apiUrlOpenFeedbacks: string = '';

  constructor(
    private readonly http: HttpClient,
    private readonly environmentService: EnvironmentService,
  ) {
    // Use environment service to get the API URL
    this.apiUrlOpenFeedbacks = this.environmentService.reviewsServiceUrl + '/open-feedbacks';
    this.apiUrlInstantFeedbacks = this.environmentService.reviewsServiceUrl + '/app/instant-feedbacks';
  }

  /**
   * Get all instant feedbacks with pagination and filtering
   */
  getAllInstantFeedbacks(pagination: PaginationSortingQuery, query: Partial<ListInstantFeedbackQuery> = {}): Observable<PaginatedInstantFeedbackUserResponse> {
    let params = new HttpParams()
      .set('page', pagination.page.toString())
      .set('size', pagination.size.toString())
      .set('getTotalCount', pagination.getTotalCount.toString());
    // Add search parameter only if it's not empty
    if (query.search && query.search.trim() !== '') {
      params = params.set('search', query.search.trim());
    }
    // Add sorting if present
    if (pagination.sortBy && pagination.orderBy) {
      params = params.set('sortBy', pagination.sortBy);
      params = params.set('orderBy', pagination.orderBy);
    }
    return this.http
      .get<PaginatedInstantFeedbackUserResponse>(this.apiUrlInstantFeedbacks, {
        params: params,
      })
      .pipe(
        map((response) => {
          // Ensure the response matches our expected structure
          return {
            totalCount: response.totalCount || 0,
            paginationInfo: {
              hasNext: response.paginationInfo?.hasNext || false,
              page: response.paginationInfo?.page || 1,
              size: response.paginationInfo?.size || 10,
              count: response.paginationInfo?.count || 0,
            },
            items: response.items || [],
          } as PaginatedInstantFeedbackUserResponse;
        }),
      );
  }

  /**
   * Get all categories with pagination and filtering
   */
  getAllOpenFeedbackCategories(pagination: PaginationSortingQuery): Observable<PaginatedOpenFeedbackCategoryResponse> {
    let params = new HttpParams()
      .set('page', pagination.page.toString())
      .set('size', pagination.size.toString())
      .set('getTotalCount', pagination.getTotalCount.toString());
    // Add sorting if present
    if (pagination.sortBy && pagination.orderBy) {
      params = params.set('sortBy', pagination.sortBy);
      params = params.set('orderBy', pagination.orderBy);
    }
    return this.http
      .get<PaginatedOpenFeedbackCategoryResponse>(`${this.apiUrlOpenFeedbacks}/categories`, {
        params: params,
      })
      .pipe(
        map((response) => {
          // Ensure the response matches our expected structure
          return {
            totalCount: response.totalCount || 0,
            paginationInfo: {
              hasNext: response.paginationInfo?.hasNext || false,
              page: response.paginationInfo?.page || 1,
              size: response.paginationInfo?.size || 10,
              count: response.paginationInfo?.count || 0,
            },
            items: response.items || [],
          } as PaginatedOpenFeedbackCategoryResponse;
        }),
      );
  }

  getLastInstantFeedbacks(): Observable<PopulatedInstantFeedbackUserResponse> {
    return this.http.get<PopulatedInstantFeedbackUserResponse>(`${this.apiUrlInstantFeedbacks}/to-vote`).pipe(
      map((response) => {
        return response;
      }),
    );
  }
  /**
   * Get all open feedbacks with pagination and filtering
   */
  getAllOpenFeedbacks(pagination: PaginationSortingQuery, query: Partial<ListOpenFeedbackQuery> = {}): Observable<PaginatedOpenFeedbackResponse> {
    let params = new HttpParams()
      .set('page', pagination.page.toString())
      .set('size', pagination.size.toString())
      .set('getTotalCount', pagination.getTotalCount.toString());
    // Add search parameter only if it's not empty
    if (query.search && query.search.trim() !== '') {
      params = params.set('search', query.search.trim());
    }
    // Add sorting if present
    if (pagination.sortBy && pagination.orderBy) {
      params = params.set('sortBy', pagination.sortBy);
      params = params.set('orderBy', pagination.orderBy);
    }
    return this.http
      .get<PaginatedOpenFeedbackResponse>(this.apiUrlOpenFeedbacks, {
        params: params,
      })
      .pipe(
        map((response) => {
          // Ensure the response matches our expected structure
          return {
            totalCount: response.totalCount || 0,
            paginationInfo: {
              hasNext: response.paginationInfo?.hasNext || false,
              page: response.paginationInfo?.page || 1,
              size: response.paginationInfo?.size || 10,
              count: response.paginationInfo?.count || 0,
            },
            items: response.items || [],
          } as PaginatedOpenFeedbackResponse;
        }),
      );
  }

  /**
   * Get a single instant feedback by ID
   */
  getInstantFeedbackById(id: string): Observable<InstantFeedbackUserResponse> {
    return this.http.get<InstantFeedbackUserResponse>(`${this.apiUrlInstantFeedbacks}/${id}`).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  /**
   * Get a single open feedback by ID
   */
  getOpenFeedbackById(id: string): Observable<OpenFeedbackResponse> {
    return this.http.get<OpenFeedbackResponse>(`${this.apiUrlOpenFeedbacks}/${id}`).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  /**
 * Create a new feedback
 */
  createOpenFeedback(feedback: Partial<CreateOpenFeedbackBody>): Observable<OpenFeedbackResponse> {
    const requestBody = {
      subject: feedback.subject,
      category: feedback.category,
      message: feedback.message,
      attachments: feedback.attachments?.length || 0 > 0 ? feedback.attachments : undefined,
    } as CreateOpenFeedbackBody;

    return this.http.post<OpenFeedbackResponse>(this.apiUrlOpenFeedbacks, requestBody).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  async putPresignedAsync(file: File, presignedUrl: string): Promise<void> {
    await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      }
    });
  }

  generateOpenFeedbackPresignedUrl(): Observable<PresignedStorageResource> {
    return this.http.post<PresignedStorageResource>(`${this.apiUrlOpenFeedbacks}/presigned-url`, {}).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  updateInstantFeedbackRatings(id: string, rating: number | undefined): Observable<InstantFeedbackRatingResponse> {
    if (rating === undefined) {
      // If rating is undefined, we skip the rating update
      return this.http.put<InstantFeedbackRatingResponse>(`${this.apiUrlInstantFeedbacks}/${id}/ratings`, {}).pipe(
        map((response) => {
          return response;
        }),
      );
    }
    const requestBody = {
      rating,
    } as InstantFeedbackRatingBody;
    return this.http.put<InstantFeedbackRatingResponse>(`${this.apiUrlInstantFeedbacks}/${id}/ratings`, requestBody).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  /**
   * Delete an instant feedback by ID
   */
  deleteInstant(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrlInstantFeedbacks}/${id}`).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  /**
   * Delete an instant feedback by ID
   */
  deleteOpen(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrlOpenFeedbacks}/${id}`).pipe(
      map((response) => {
        return response;
      }),
    );
  }
}
